import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, RefreshCw, TrendingUp, Phone, CheckCircle, AlertTriangle, FileText, Lock, Unlock, ExternalLink } from 'lucide-react';

// Tela COBRANCA - Neurotech-X 24/10/2026
// Visao geral do modelo novo (aba Servicos + telefonia): tudo vem calculado na hora de
// GET /api/cobranca/visao, nada e gravado por esta tela. A unica acao de escrita e a chave
// "Atendimento suspenso" (PUT /api/clientes/:id), a mesma da tela Clientes.
// O modelo antigo de assinaturas saiu desta tela; os dados antigos continuam no banco.

function getToken() { return localStorage.getItem('synapse_token'); }

async function apiFetch(path, opts = {}) {
    var res = await fetch(path, {
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
        ...opts,
    });
    var dados = await res.json().catch(function () { return {}; });
    if (!res.ok) throw new Error((dados && dados.error) || ('HTTP ' + res.status));
    return dados;
}

function formatBRL(val) {
    var num = parseFloat(val) || 0;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function br(iso) { return iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—'; }

function Cartao({ cor, icone, titulo, valor }) {
    var Icone = icone;
    return (
        <div className="card" style={{ borderLeft: '3px solid ' + cor }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Icone size={16} style={{ color: cor }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{titulo}</span>
            </div>
            <div className="stat-number" style={{ color: cor }}>{valor}</div>
        </div>
    );
}

function Situacao({ c }) {
    if (c.situacao === 'suspenso_chave') {
        return <span className="badge" style={{ background: 'rgba(226,75,74,0.15)', color: 'var(--red-500)' }}><Lock size={13} /> Suspenso (chave)</span>;
    }
    if (c.situacao === 'travado_fatura') {
        return <span className="badge" style={{ background: 'rgba(226,75,74,0.15)', color: 'var(--red-500)' }}><AlertTriangle size={13} /> Vencida desde {br(c.vencida_desde)}</span>;
    }
    if (c.situacao === 'em_dia') {
        return <span className="badge" style={{ background: 'rgba(29,158,117,0.15)', color: 'var(--green-400)' }}><CheckCircle size={13} /> Em dia</span>;
    }
    return <span className="badge" style={{ background: 'rgba(107,106,99,0.15)', color: 'var(--text-muted)' }}>Sem cobrança</span>;
}

export default function Billing() {
    var [visao, setVisao] = useState(null);
    var [loading, setLoading] = useState(true);
    var [erro, setErro] = useState('');
    var [avisoAdmin, setAvisoAdmin] = useState('');
    var [salvandoAviso, setSalvandoAviso] = useState(false);
    var [mudandoChave, setMudandoChave] = useState(null); // id do cliente com chave em mudanca

    var fetchAll = useCallback(async function () {
        setLoading(true); setErro('');
        try {
            var v = await apiFetch('/api/cobranca/visao');
            setVisao(v);
            try { var cfgA = await apiFetch('/api/billing/config-aviso'); setAvisoAdmin((cfgA && cfgA.valor) || ''); } catch (eC) {}
        } catch (e) { setErro('Não carregou: ' + e.message); }
        setLoading(false);
    }, []);

    useEffect(function () { fetchAll(); }, [fetchAll]);

    async function mudarChave(c) {
        var ligar = !c.chave_suspenso;
        var msg = ligar
            ? 'SUSPENDER o atendimento de ' + c.nome + '? Os agentes param de responder em todos os canais.'
            : 'LIBERAR o atendimento de ' + c.nome + '?' + (c.situacao === 'travado_fatura' || (c.abertas || []).some(function (f) { return visao && f.vencimento < visao.hoje; }) ? ' Atenção: há fatura vencida — a trava automática continua até marcar como paga.' : '');
        if (!window.confirm(msg)) return;
        setMudandoChave(c.id);
        try {
            await apiFetch('/api/clientes/' + c.id, { method: 'PUT', body: JSON.stringify({ atendimento_suspenso: ligar }) });
            await fetchAll();
        } catch (e) { alert('Não salvou: ' + e.message); }
        setMudandoChave(null);
    }

    var clientes = (visao && visao.clientes) || [];

    return (
        <div>
            {/* Cabecalho */}
            <div className="page-header">
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <CreditCard size={24} style={{ color: 'var(--purple-400)' }} /> Cobrança
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>
                        Visão geral do que está configurado na aba <Link to="/servicos" style={{ color: 'var(--purple-400)' }}>Serviços</Link> e na telefonia. Faturas e pagamentos são marcados lá.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <Link to="/servicos" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                        <ExternalLink size={16} /> Abrir Serviços
                    </Link>
                    <button className="btn-ghost" onClick={fetchAll} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <RefreshCw size={16} /> Atualizar
                    </button>
                </div>
            </div>

            {/* WhatsApp de aviso (admin) */}
            <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 220 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>WhatsApp de aviso (admin)</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Recebe copia de todo aviso enviado aos clientes. Numeros com codigo do pais, separados por virgula.</div>
                </div>
                <input value={avisoAdmin} onChange={function (e) { setAvisoAdmin(e.target.value); }} placeholder="5511999999999, 14075551234" style={{ flex: 1, minWidth: 240 }} />
                <button className="btn-primary" disabled={salvandoAviso} onClick={async function () { setSalvandoAviso(true); try { await apiFetch('/api/billing/config-aviso', { method: 'PUT', body: JSON.stringify({ valor: avisoAdmin }) }); } catch (e) { alert('Erro ao salvar'); } setSalvandoAviso(false); }}>Salvar</button>
            </div>

            {/* Cartoes */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 28 }}>
                <Cartao cor="var(--green-400)" icone={TrendingUp} titulo="Receita fixa mensal" valor={visao ? formatBRL(visao.receita_fixa_mensal) : '—'} />
                <Cartao cor="var(--purple-400)" icone={Phone} titulo="Telefonia (último fechamento)" valor={visao ? formatBRL(visao.telefonia_ultimo_fechamento) : '—'} />
                <Cartao cor="var(--green-400)" icone={CheckCircle} titulo="Em dia" valor={visao ? visao.em_dia : '—'} />
                <Cartao cor="var(--red-500)" icone={AlertTriangle} titulo="Travados" valor={visao ? visao.travados : '—'} />
                <Cartao cor="var(--amber-500)" icone={FileText} titulo="Faturas em aberto" valor={visao ? visao.faturas_abertas : '—'} />
            </div>

            {erro && <div className="card" style={{ marginBottom: 16, color: 'var(--red-500)' }}>{erro}</div>}

            {/* Tabela: 1 cliente por linha */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table>
                    <thead>
                        <tr>
                            <th>Cliente</th>
                            <th>Produtos</th>
                            <th>Próxima fatura</th>
                            <th>Situação</th>
                            <th>Em aberto</th>
                            <th>Última paga</th>
                            <th>Chave</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Carregando...</td></tr>
                        ) : clientes.length === 0 ? (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Nenhum cliente. Cadastre os serviços na aba Serviços.</td></tr>
                        ) : clientes.map(function (c) {
                            var envios = c.produtos.filter(function (p) { return p.envia && p.dia_envio; });
                            return (
                                <tr key={c.id} style={c.situacao === 'suspenso_chave' || c.situacao === 'travado_fatura' ? { background: 'rgba(226,75,74,0.05)' } : undefined}>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{c.nome}</div>
                                        {!c.ativo && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>cliente inativo</div>}
                                    </td>
                                    <td>
                                        {c.produtos.length === 0 ? <span style={{ color: 'var(--text-muted)' }}>—</span> : c.produtos.map(function (p, i) {
                                            return (
                                                <div key={i} style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'baseline' }}>
                                                    <span>{p.produto}</span>
                                                    <span style={{ color: 'var(--text-muted)' }}>{p.variavel ? 'variável (minutos)' : formatBRL(p.valor)}</span>
                                                    {!p.envia && <span style={{ fontSize: 11, color: 'var(--amber-500)' }}>envio desligado</span>}
                                                </div>
                                            );
                                        })}
                                    </td>
                                    <td>
                                        {envios.length === 0 ? <span style={{ color: 'var(--text-muted)' }}>—</span> : envios.map(function (p, i) {
                                            return <div key={i} style={{ fontSize: 13 }}>{p.produto}: dia {p.dia_envio}</div>;
                                        })}
                                    </td>
                                    <td><Situacao c={c} /></td>
                                    <td>
                                        {(c.abertas || []).length === 0 ? <span style={{ color: 'var(--text-muted)' }}>—</span> : c.abertas.map(function (f, i) {
                                            var vencida = visao && f.vencimento && f.vencimento < visao.hoje;
                                            return (
                                                <div key={i} style={{ fontSize: 13, color: vencida ? 'var(--red-500)' : undefined }}>
                                                    {f.tipo} · {formatBRL(f.valor)} · vence {br(f.vencimento)}
                                                </div>
                                            );
                                        })}
                                    </td>
                                    <td>{c.ultima_paga ? br(c.ultima_paga) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                    <td>
                                        {c.chave_suspenso ? (
                                            <button type="button" className="btn-ghost" disabled={mudandoChave === c.id} style={{ padding: '6px 12px', borderColor: 'var(--green-400)', color: 'var(--green-400)', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={function () { mudarChave(c); }}>
                                                <Unlock size={14} /> {mudandoChave === c.id ? 'Salvando…' : 'Liberar'}
                                            </button>
                                        ) : (
                                            <button type="button" className="btn-ghost" disabled={mudandoChave === c.id} style={{ padding: '6px 12px', borderColor: 'var(--red-500)', color: 'var(--red-500)', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={function () { mudarChave(c); }}>
                                                <Lock size={14} /> {mudandoChave === c.id ? 'Salvando…' : 'Suspender'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
                Situação calculada na hora, com o mesmo critério da trava automática: chave ligada ou fatura vencida sem pagamento = agentes parados.
                O pagamento é marcado na aba Serviços (ou pelo NeuroX). A telefonia é variável: a fatura sai quando o ciclo fecha.
            </p>
        </div>
    );
}
